import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import { Marker, Popup } from "react-map-gl/mapbox";

import PropertyCard from "../../sideMenu/propertyCard";

import styles from "./propertyMarkers.module.css";

const PropertyMarkers = memo(function PropertyMarkers({
  properties,
  markerInfo,
  mapRef,
}) {
  const [hovered, setHovered] = useState(null);
  const [visibleProperties, setVisibleProperties] = useState([]);
  const leaveTimer = useRef(null);

  const cancelLeave = useCallback(() => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
  }, []);

  const scheduleLeave = useCallback(() => {
    cancelLeave();
    leaveTimer.current = setTimeout(() => setHovered(null), 250);
  }, [cancelLeave]);

  const handleEnter = useCallback(
    (p) => {
      cancelLeave();
      setHovered(p);
    },
    [cancelLeave]
  );

  useEffect(() => () => cancelLeave(), [cancelLeave]);

  // Create a stable markerInfo identifier to prevent unnecessary re-renders
  const markerInfoId = React.useMemo(() => {
    if (!markerInfo) return null;
    return `${markerInfo.lat}-${markerInfo.lng}`;
  }, [markerInfo]);

  // Function to filter properties based on current viewport
  const filterPropertiesByViewport = useCallback(() => {
    if (!mapRef?.current || !properties?.length) {
      setVisibleProperties([]);
      return;
    }

    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();

      // Add a buffer to the bounds to preload markers slightly outside viewport
      const buffer = 0.01; // Adjust this value based on your needs
      const bufferedBounds = {
        north: bounds.getNorth() + buffer,
        south: bounds.getSouth() - buffer,
        east: bounds.getEast() + buffer,
        west: bounds.getWest() - buffer,
      };

      const visible = properties.filter((property) => {
        if (!property.lat || !property.lng) return false;

        const lat = Number(property.lat);
        const lng = Number(property.lng);

        return (
          lat >= bufferedBounds.south &&
          lat <= bufferedBounds.north &&
          lng >= bufferedBounds.west &&
          lng <= bufferedBounds.east
        );
      });

      // Limit to a reasonable number of visible markers for performance
      const maxVisibleMarkers = 1000;
      const limitedVisible = visible.slice(0, maxVisibleMarkers);

      setVisibleProperties(limitedVisible);
    } catch (error) {
      console.error("Error filtering properties by viewport:", error);
      // Fallback: show first 500 properties if viewport filtering fails
      setVisibleProperties(properties.slice(0, 500));
    }
  }, [properties, mapRef]);

  // Update visible properties when map moves or properties change
  useEffect(() => {
    filterPropertiesByViewport();
  }, [filterPropertiesByViewport]);

  // Listen to map move events to update visible properties
  useEffect(() => {
    if (!mapRef?.current) return;

    const map = mapRef.current.getMap();

    const handleMove = () => {
      // Debounce the viewport update to avoid excessive filtering
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
      }
      leaveTimer.current = setTimeout(filterPropertiesByViewport, 200);
    };

    map.on("move", handleMove);
    map.on("zoom", handleMove);

    return () => {
      map.off("move", handleMove);
      map.off("zoom", handleMove);
    };
  }, [mapRef, filterPropertiesByViewport]);

  // Memoize the marker components to prevent unnecessary re-renders
  const markerElements = React.useMemo(() => {
    return visibleProperties.map((property, index) => {
      const propertyId = `${property.lat}-${property.lng}`;
      const isSelected = markerInfoId === propertyId;

      return (
        <Marker
          key={property.id || `${property.address}-${index}`}
          latitude={Number(property.lat)}
          longitude={Number(property.lng)}
          anchor="center"
        >
          <div
            onMouseEnter={() => handleEnter(property)}
            onMouseLeave={scheduleLeave}
            role="button"
            tabIndex={0}
            style={{
              width: isSelected ? 20 : 16,
              height: isSelected ? 20 : 16,
              borderRadius: "50%",
              background: isSelected ? "#ff4444" : "#64b2e6",
              border: "2px solid #fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              pointerEvents: "auto",
            }}
          />
        </Marker>
      );
    });
  }, [visibleProperties, markerInfoId, handleEnter, scheduleLeave]);

  return (
    <>
      {markerElements}

      {hovered && hovered.lat && hovered.lng && (
        <Popup
          latitude={Number(hovered.lat)}
          longitude={Number(hovered.lng)}
          closeButton={false}
          closeOnClick={false}
          closeOnMove={false}
          anchor="top"
          offset={15}
          maxWidth="600px"
          className={styles.popup}
        >
          <div onMouseEnter={cancelLeave} onMouseLeave={scheduleLeave}>
            <PropertyCard {...hovered} />
          </div>
        </Popup>
      )}
    </>
  );
});

export default PropertyMarkers;
